import { Check, RadioButtonChecked, WrapTextTwoTone, ClearSharp, MoreHorizOutlined, HourglassTopRounded } from '@mui/icons-material';

import { LoadingButton } from '@mui/lab';
import { Box, Grid, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { FieldValues, useForm } from 'react-hook-form';
import AppAccordion from '../../../Component/Common/Components/Accordion/AppAccordion';
import { AppDatepicker } from '../../../Component/Common/Components/FormValidationInputs/AppDatepicker';
import { AppQuill } from '../../../Component/Common/Components/FormValidationInputs/AppQuill';
import AppSelectList, { IDropDownOptions } from '../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import { ISteps } from '../../../Component/Common/Components/Stepper/Stepper';
import colors from '../../../assets/theme/base/colors';
import { PortalDocumentsDropZone } from '../../Common/PortalDocuments/PortalDocumentsDropZone';
import { VerificationClientStatus, VerificationClientStatusState } from '../../Common/StaticData/VAClientStatus';
import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { clientStatusSchema } from './ClientStatusValidationSchema';
import { ApplicationStatusOptionsDto } from '../../../Component/Model/Dto/VerificationAgency/VerificationAgencyClientApplication';
import { objectToDropdownOptions } from '../../../Helpers/objectHelper';


const { primary, warning, error, success, secondary } = colors;

interface IProps {
    step: ISteps;
    riskAssessmentFlags: { text: string; value: boolean }[];
    riskAssessmentNotes?: string;
    link: string;
    updateStatus: (data: FieldValues) => void;
    options?: ApplicationStatusOptionsDto;
}

export const StatusCard = (props: IProps) => {
    const [verificationProgressOptionsList, setVerificationProgressOptionsList] = useState<IDropDownOptions[]>([]);
    const [enquiryStatusOptionsList, setEnquiryStatusOptionsList] = useState<IDropDownOptions[]>([]);
    const [applicationStatusOptionsList, setApplicationStatusOptionsList] = useState<IDropDownOptions[]>([]);
    const [proposalAgreementStatusOptionsList, setProposalAgreementStatusOptionsList] = useState<IDropDownOptions[]>([]);
    const [invoiceStatusOptionsList, setInvoiceStatusOptionsList] = useState<IDropDownOptions[]>([]);
    const [wrapUpOptionsList, setWrapUpOptionsList] = useState<IDropDownOptions[]>([]);
    const [defaultOptionsList, setDefaultOptionsList] = useState<IDropDownOptions[]>([]);
    const [verificationAgreementOptionsList, setVerificationAgreementOptionsList] = useState<IDropDownOptions[]>([]);

    const { control, handleSubmit, formState: { isSubmitting }, reset } = useForm({
        mode: 'all',
        resolver: yupResolver(clientStatusSchema)
    });

    const handleDataSubmit = (data: FieldValues) => {
        try {
            const validationData = { ...data };
            props.updateStatus(validationData);
        } catch (error) {
            console.log(error)
        }
    };

    const resetFormData = () => {
        const formData = {
            clientStatus: props.step.id,
            status: props.step.state,
            description: props.step.description,
            date: props.step.dateAchieved,
            notes: props.riskAssessmentNotes
        };
        reset(formData);
    }

    const setDropdownOptions = (options: ApplicationStatusOptionsDto) => {
        const verificationProgressOptions = objectToDropdownOptions(options.verificationProgressStatuses);
        const enquiryStatusOptions = objectToDropdownOptions(options.enquiryStatuses);
        const applicationStatusOptions = objectToDropdownOptions(options.applicationStatuses);
        const proposalAgreementStatusOptions = objectToDropdownOptions(options.proposalStatuses);
        const invoiceStatusOptions = objectToDropdownOptions(options.invoiceStatuses);
        const wrapUpStatusOptions = objectToDropdownOptions(options.wrapUpStatuses);
        const defaultStatusOptions = objectToDropdownOptions(options.defaultStatuses);
        const verificationAgreementOptions = objectToDropdownOptions(options.verificationAgreementStatuses);

        setVerificationProgressOptionsList(verificationProgressOptions);
        setEnquiryStatusOptionsList(enquiryStatusOptions);
        setApplicationStatusOptionsList(applicationStatusOptions);
        setProposalAgreementStatusOptionsList(proposalAgreementStatusOptions);
        setInvoiceStatusOptionsList(invoiceStatusOptions);
        setWrapUpOptionsList(wrapUpStatusOptions);
        setDefaultOptionsList(defaultStatusOptions);
        setVerificationAgreementOptionsList(verificationAgreementOptions);
    }
    
    useEffect(() => {
        if (props.options) {
            setDropdownOptions(props.options);
        }
    }, []);

    useEffect(() => {
        resetFormData();
    }, [props.step]);

    const getAvatar = (state: number) => {
        return (
            <>
                {state === VerificationClientStatusState.Complete || state === VerificationClientStatusState.Not_Applicable ? (
                    <div style={{
                        backgroundColor: '#fff',
                        borderColor: primary.main,
                        zIndex: 1,
                        width: 35,
                        height: 35,
                        display: 'flex',
                        borderRadius: '50%',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}>
                        <Check style={{ color: success.main }} />
                    </div>

                ) : state === VerificationClientStatusState.Terminated ? (
                    <div style={{
                        backgroundColor: 'white',
                        borderColor: primary.main,
                        zIndex: 1,
                        width: 35,
                        height: 35,
                        display: 'flex',
                        borderRadius: '50%',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}>
                        <ClearSharp style={{ color: error.main}} />
                    </div>
                ) : state === VerificationClientStatusState.In_Progress_Stage_1 || state === VerificationClientStatusState.In_Progress_Stage_2 ? (
                    <div style={{
                        backgroundColor: 'white',
                        borderColor: primary.main,
                        zIndex: 1,
                        width: 35,
                        height: 35,
                        display: 'flex',
                        borderRadius: '50%',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}>                       
                        <HourglassTopRounded style={{ color: warning.main }} />
                    </div>
                ) : (
                    <div style={{
                        backgroundColor: 'white',
                        borderColor: primary.main,
                        zIndex: 1,
                        width: 35,
                        height: 35,
                        display: 'flex',
                        borderRadius: '50%',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}>
                        <div style={{
                            width: '70%',
                            height: '70%',
                            display: 'flex',
                            borderRadius: '70%',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: 'white',
                            borderColor: primary.main,
                            borderStyle: 'solid',
                            borderWidth: 3
                        }}></div>
                    </div>
                )
                }
            </>
        );
    }

    return (
        <>
            <Grid id={`step-${props.step.id}-form`} component='form' onSubmit={handleSubmit(handleDataSubmit)} mt={2}>
                <AppAccordion label={props.step.label}
                    textColor={secondary.main}
                    expandOnDefault={false}
                    backgroundColor='white'
                    iconFill={props.step.state === VerificationClientStatusState.Complete ? success.main :
                        props.step.state === VerificationClientStatusState.In_Progress_Stage_1 || props.step.state === VerificationClientStatusState.In_Progress_Stage_2 ? warning.main :
                            props.step.state === VerificationClientStatusState.Terminated ? error.main : primary.main}
                    avatar={getAvatar(props.step.state)}
                >
                    {(props.step.id == VerificationClientStatus.Enquiry_Received) && (
                        <Box mt={2}>
                            <AppSelectList control={control} name={"status"} label="Status" items={enquiryStatusOptionsList} defaultValue={props.step.state || 0} disabled={true} />
                        </Box>
                    )}
                    {(props.step.id == VerificationClientStatus.Application) && (
                        <Box mt={2}>
                            <AppSelectList control={control} name={"status"} label="Status" items={applicationStatusOptionsList} defaultValue={props.step.state || 0} />
                        </Box>
                    )}
                    {(props.step.id == VerificationClientStatus.Proposal) && (
                        <Box mt={2}>
                            <AppSelectList control={control} name={"status"} label="Status" items={proposalAgreementStatusOptionsList} defaultValue={props.step.state || 0} />
                        </Box>
                    )}
                    {(props.step.id == VerificationClientStatus.Verification_Agreement) && (
                        <Box mt={2}>
                            <AppSelectList control={control} name={"status"} label="Status" items={verificationAgreementOptionsList} defaultValue={props.step.state || 0} />
                        </Box>
                    )}
                    {(props.step.id == VerificationClientStatus.Risk_Assessment) && (
                        <Box mt={2}>
                            <AppSelectList control={control} name={"status"} label="Status" items={defaultOptionsList} defaultValue={props.step.state || 0} />
                        </Box>
                    )}
                    {(props.step.id == VerificationClientStatus.Verification_Progress) && (
                        <Box mt={2}>
                            <AppSelectList control={control} name={"status"} label="Status" items={verificationProgressOptionsList} defaultValue={props.step.state || 0} />
                        </Box>
                    )}
                    {(props.step.id == VerificationClientStatus.Invoice) && (
                        <Box mt={2}>
                            <AppSelectList control={control} name={"status"} label="Status" items={invoiceStatusOptionsList} defaultValue={props.step.state || 0} />
                        </Box>
                    )}
                    {(props.step.id == VerificationClientStatus.Wrap_Up) && (
                        <Box mt={2}>
                            <AppSelectList control={control} name={"status"} label="Status" items={wrapUpOptionsList} defaultValue={props.step.state || 0} />
                        </Box>
                    )}
                    <Box mt={2}>
                        <AppDatepicker control={control} name={"date"} label="Date Achieved" inputFormat='yyyy/MM/dd' defaultValue={props.step.dateAchieved} />
                    </Box>
                    {props.step.id === VerificationClientStatus.Risk_Assessment && (
                        <>
                            <Box mt={2} sx={{ border: '1px solid #ccc', borderRadius: '5px', padding: '10px' }}>
                                <List >
                                    <Typography variant="body2" style={{ fontSize: '0.875rem' }}>
                                        Summary:
                                    </Typography>
                                    {props.riskAssessmentFlags.filter((field) => field.value).map((field, index) => (
                                        <ListItem key={index}>
                                            <ListItemIcon>
                                                <RadioButtonChecked style={{ fontSize: '16px', marginRight: '10px', color: '#000' }} />
                                            </ListItemIcon>
                                            <ListItemText>
                                                <Typography variant="body2" style={{ fontSize: '0.875rem' }}>
                                                    {field.text}
                                                </Typography>
                                            </ListItemText>
                                        </ListItem>
                                    ))}
                                </List>
                                <Box mt={2} >
                                    <Typography variant="body2" style={{ fontSize: '0.875rem' }}>
                                        The following note(s) were provided:
                                    </Typography>
                                    <AppQuill control={control} name={"notes"} placeholder="Notes:" disabled={true} />
                                </Box>
                            </Box>
                        </>
                    )}
                    {
                        props.step.recordId > 0 &&
                        (props.step.id == VerificationClientStatus.Verification_Agreement || props.step.id == VerificationClientStatus.Invoice) &&
                        <Box mt={2}>
                            <PortalDocumentsDropZone link={props.link} id={props.step.recordId} />
                        </Box>
                    }
                    {(
                        (props.step.id > VerificationClientStatus.Application && props.step.id <= VerificationClientStatus.Proposal) ||
                        props.step.id == VerificationClientStatus.Verification_Agreement ||
                        props.step.id == VerificationClientStatus.Invoice
                    )
                        && (
                            <Box mt={2}>
                                <AppQuill control={control} name={"description"} placeholder="Description:" defaultValue={props.step.description || ''} />
                            </Box>
                        )}
                    <Box mt={2}>
                        <LoadingButton
                            loading={isSubmitting}
                            variant='contained'
                            type="submit"
                            color='primary'
                            form={`step-${props.step.id}-form`}
                        >
                            Save
                        </LoadingButton>
                    </Box>
                </AppAccordion>
            </Grid >
        </>
    )
}
